import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGetCustomRoles } from '../hooks/useGetCustomRoles';
import { NewPageWrapper, RichTable, useTitle } from '../../../common';
import { useBreadcrumbsActions } from '../../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { RefreshButton } from '../../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { ErrorResponse } from '../../../common/types/errorRespose';
import { CustomRoleDto } from '../../../api-client';
import { useSearch } from '../../../common/hooks/useSearch/useSearch';
import { FuseOptions } from 'fuse.js';
import { CustomRolePermissionsRow } from './ui/CustomRolePermissionsRow/CustomRolePermissionsRow';
import { useHistory } from 'react-router-dom';
import { CreateButton } from '../../../common/components/Breadcrumbs/buttons/CreateButton/CreateButton';
import { CreateCustomRoleModal } from './ui/CreateCustomRoleModal/CreateCustomRoleModal';
import { useCreateCustomRole } from '../hooks/useCreateCustomRole';
import { useQueryClient } from 'react-query';
import { NotificationsContext } from '../../../common/notifications/Notifications';
import { Button } from '@hiiretail/synergy-ui';

export const CustomRolesList: React.FC = () => {
  useTitle('Custom Roles');
  const [selectedRole, setSelectedRole] = useState<CustomRoleDto | null>(null);
  const [isCreateCustomRoleModalOpen, setIsCreateCustomRoleModalOpen] = useState(false);
  const { updateActions } = useBreadcrumbsActions();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationsContext);

  useEffect(() => {
    updateActions(
      <>
        <RefreshButton onClick={refetchCustomRoles} permission="iam.custom-role.list" />
        <CreateButton
          title="New custom role"
          onClick={toggleCreateCustomRoleModalOpen}
          permission="iam.custom-role.create"
        />
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: customRoles,
    isFetching: isLoading,
    error: fetchError,
    refetch: refetchCustomRoles,
  } = useGetCustomRoles({
    cacheTime: 50000,
    staleTime: Infinity,
    keepPreviousData: false,
    retry: false,
  });

  const {
    mutate: createCustomRole,
    isLoading: isCreating,
    error: createError,
  } = useCreateCustomRole({
    onSuccess: ({ data: { id } }) => {
      showNotification(`Custom role ${id} created successfully`);
      queryClient.invalidateQueries('custom-roles').then();
      toggleCreateCustomRoleModalOpen();
      history.push(`custom-roles/${id}`);
    },
  });

  const searchOptions: FuseOptions<CustomRoleDto> = useMemo(
    () => ({
      keys: ['id', 'name'],
      threshold: 0.1,
      caseSensitive: false,
      findAllMatches: true,
    }),
    [],
  );

  const { filteredData: filteredBuiltInRoles, onSearchChange } = useSearch(
    customRoles as CustomRoleDto[],
    searchOptions,
  );

  const tableColumns = useMemo(
    () => [
      { Header: 'ID', accessor: 'id', width: 100 },
      { Header: 'Name', accessor: 'name', width: 100 },
      {
        Header: 'Permissions',
        accessor: 'permissions',
        width: 200,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: CustomRoleDto;
          };
        }) => <CustomRolePermissionsRow customRole={original} />,
      },
      {
        Header: 'Actions',
        width: 50,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: CustomRoleDto;
          };
        }) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRole(original);
              toggleCreateCustomRoleModalOpen();
            }}
            variant="secondary"
          >
            Clone
          </Button>
        ),
      },
    ],
    [],
  );

  const handleRowClick = (data: TableData) => {
    history.push(`custom-roles/${(data as unknown as CustomRoleDto).id}`);
  };

  const toggleCreateCustomRoleModalOpen = () => {
    setIsCreateCustomRoleModalOpen((prevState) => !prevState);
  };

  const onCreateCustomRoleClose = () => {
    setSelectedRole(null);
    toggleCreateCustomRoleModalOpen();
  };

  return (
    <NewPageWrapper>
      {isCreateCustomRoleModalOpen && (
        <CreateCustomRoleModal
          onClose={onCreateCustomRoleClose}
          isOpen={isCreateCustomRoleModalOpen}
          isCreating={isCreating}
          error={createError}
          createCustomRole={createCustomRole}
          selectedRole={selectedRole}
        />
      )}
      <RichTable
        id="custom-roles"
        onRowClick={handleRowClick}
        title="Browse custom roles"
        columns={tableColumns}
        data={filteredBuiltInRoles as unknown as TableData[]}
        isLoading={isLoading}
        fetchError={fetchError as ErrorResponse | null}
        refetch={refetchCustomRoles}
        errorSubject="custom roles"
        searchFieldLabel="Filter by id or name"
        onSearchChange={onSearchChange}
        permissions={{
          list: 'iam.custom-role.list',
          create: 'iam.custom-role.create',
        }}
      />
    </NewPageWrapper>
  );
};
